// Model tools of step 1 (phase M2c): change the model (Zeeman field, sublattice mass, on-site shifts, scissor) and
// look at its band edges - in the field, against |B| and against the field direction (2D materials: in plane vs
// out of plane), with valley edges and g factors.
import { Alert, Badge, Button, Card, Checkbox, Grid, Group, MultiSelect, NumberInput, SegmentedControl, Select, Stack, Table, Tabs,
  Text, TextInput } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { JobProgress, JobStatusBadge } from "../../components/Jobs";
import { Plot } from "../../components/Plot";
import { PreviousRuns, RunCard } from "../../components/RunCard";
import { download, post, type Job, type Limits, type ProjectDetail } from "../../lib/api";
import { useLiveJob } from "../../lib/hooks";
import { ANALYSES, bandZoomFigure, defaultEdges, defaultModify, describeEdges, describeModifiers, edgesProblems, edgesRequest, fmtE, fmtK,
  modifiersOf, modifyProblems, slopeRows, sweepFigure, valleyLevelsFigure, type Analysis, type EdgesForm, type ModifyForm } from "../../lib/fields";
import type { ModelSummary, NegfMemory } from "../../lib/negf";

function N({ label, value, onChange, unit, step, int = false, min, w }: {
  label: string; value: number; onChange: (v: number) => void; unit?: string; step?: number; int?: boolean; min?: number; w?: number;
}) {
  return <NumberInput label={label} size="xs" w={w} value={value} step={step} min={min} allowDecimal={!int} decimalScale={int ? 0 : 6}
    rightSection={unit ? <Text size="xs" c="dimmed">{unit}</Text> : undefined} rightSectionWidth={unit ? 40 : undefined}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

function Vec({ label, value, onChange, unit, step }: { label: string; value: number[]; onChange: (v: number[]) => void; unit: string; step: number }) {
  return (
    <Group grow gap="xs">
      {["x", "y", "z"].map((a, i) => <N key={a} label={`${label}${a}`} unit={unit} step={step} value={value[i] ?? 0}
        onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} />)}
    </Group>
  );
}

// -- modify -------------------------------------------------------------------------------------------------------
export function ModifyPanel({ project, mem, remember, edit, product = "negf" }: {
  project: ProjectDetail; mem: NegfMemory; remember: (m: Partial<NegfMemory>) => void; edit: (m: Partial<NegfMemory>) => void;
  product?: "negf" | "properties";
}) {
  const qc = useQueryClient();
  const base = mem.base_summary ?? mem.summary!;
  const form = mem.modify ?? defaultModify(base.n_sites);
  const set = (next: Partial<ModifyForm>) => edit({ modify: { ...form, ...next } });
  const { job } = useLiveJob(mem.modify_job);
  const atoms = base.species.map((el, i) => ({ value: String(i), label: `${i + 1}: ${el}` }));
  const problems = modifyProblems(form, base.n_sites, base.two_d !== false);
  const elements = [...new Set(base.species)];

  // a finished modify job: use its model (once; Reset clears modify_job)
  useEffect(() => {
    if (job?.status === "succeeded" && job.id === mem.modify_job && job.result?.model_file && job.result.model_file !== mem.model_file)
      remember({ model_file: job.result.model_file, summary: job.result.summary as ModelSummary, modify_job: undefined });
  }, [job, mem.modify_job, mem.model_file, remember]);

  const apply = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: `${product}.modify`, project_id: project.id,
      params: { model_file: mem.base_file ?? mem.model_file, modifiers: modifiersOf(form, base.reach) } }),
    onSuccess: (j) => {
      remember({ modify_job: j.id, base_file: mem.base_file ?? mem.model_file, base_summary: mem.base_summary ?? mem.summary });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (e) => notifyError(e),
  });
  const reset = () => remember({ model_file: mem.base_file, summary: mem.base_summary, base_file: undefined, base_summary: undefined,
    modify_job: undefined });
  const changes = describeModifiers(mem.summary?.modifiers);

  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">Changes to the model itself, used by every device and calculation after it. They are applied to the
        model as built (not on top of earlier changes); Reset goes back to it.</Text>
      <Card withBorder p="xs">
        <Checkbox label="Zeeman field (spin and orbital moments)" checked={form.zeeman.on} onChange={(e) => set({ zeeman: { ...form.zeeman, on: e.currentTarget.checked } })} />
        {form.zeeman.on && (
          <Stack gap={4} mt={4}>
            <Vec label="B" unit="T" step={1} value={form.zeeman.B} onChange={(B) => set({ zeeman: { ...form.zeeman, B } })} />
            <Group gap="md" align="flex-end">
              <N label="g" w={110} step={0.1} value={form.zeeman.g} onChange={(g) => set({ zeeman: { ...form.zeeman, g } })} />
              <Checkbox size="xs" label="spin (g μB/2) B·σ" checked={form.zeeman.spin} onChange={(e) => set({ zeeman: { ...form.zeeman, spin: e.currentTarget.checked } })} />
              <Checkbox size="xs" label="orbital μB L·B" checked={form.zeeman.orbital} onChange={(e) => set({ zeeman: { ...form.zeeman, orbital: e.currentTarget.checked } })} />
            </Group>
            <Text size="xs" c="dimmed">No orbital motion here (that is the Peierls field of a calculation). The band-edge tools below sweep B themselves.</Text>
          </Stack>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Sublattice mass (opens a gap Δ at Dirac points)" checked={form.mass.on} onChange={(e) => set({ mass: { ...form.mass, on: e.currentTarget.checked } })} />
        {form.mass.on && (
          <Group grow gap="xs" mt={4} align="flex-end">
            <N label="Δ" unit="eV" step={0.05} value={form.mass.delta} onChange={(delta) => set({ mass: { ...form.mass, delta } })} />
            <MultiSelect size="xs" label="+Δ/2 on" data={atoms} value={form.mass.plus.map(String)} onChange={(v) => set({ mass: { ...form.mass, plus: v.map(Number) } })} />
            <MultiSelect size="xs" label="−Δ/2 on" data={atoms} value={form.mass.minus.map(String)} onChange={(v) => set({ mass: { ...form.mass, minus: v.map(Number) } })} />
          </Group>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="On-site shift of some orbitals" checked={form.shift.on} onChange={(e) => set({ shift: { ...form.shift, on: e.currentTarget.checked } })} />
        {form.shift.on && (
          <Group grow gap="xs" mt={4} align="flex-end">
            <N label="shift" unit="eV" step={0.05} value={form.shift.shift} onChange={(shift) => set({ shift: { ...form.shift, shift } })} />
            <Select size="xs" label="element" data={[{ value: "", label: "all" }, ...elements.map((e) => ({ value: e, label: e }))]} value={form.shift.element}
              onChange={(v) => set({ shift: { ...form.shift, element: v ?? "" } })} allowDeselect={false} />
            <TextInput size="xs" label="orbitals (labels)" placeholder="e.g. dz2 dxy" value={form.shift.orbitals}
              onChange={(e) => set({ shift: { ...form.shift, orbitals: e.currentTarget.value } })} />
          </Group>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Atomic spin-orbit coupling λ L·S (a model made without SOC)" checked={!!form.soc?.on} data-testid="modify-soc"
          onChange={(e) => set({ soc: { ...(form.soc ?? defaultModify().soc!), element: form.soc?.element || elements[0] || "", on: e.currentTarget.checked } })} />
        {form.soc?.on && (
          <Stack gap={4} mt={4}>
            <Group grow gap="xs" align="flex-end">
              <Select size="xs" label="element" data={elements.map((e) => ({ value: e, label: e }))} value={form.soc.element}
                onChange={(v) => set({ soc: { ...form.soc!, element: v ?? "" } })} allowDeselect={false} />
              <Select size="xs" label="shell" value={form.soc.family} allowDeselect={false}
                data={[{ value: "p", label: "p" }, { value: "d", label: "d" }, { value: "f", label: "f" }, { value: "all", label: "every p, d, f" }]}
                onChange={(v) => set({ soc: { ...form.soc!, family: (v ?? "p") as "p" | "d" | "f" | "all" } })} />
              <N label="λ" unit="eV" step={0.05} value={form.soc.lam} onChange={(lam) => set({ soc: { ...form.soc!, lam } })} />
            </Group>
            <TextInput size="xs" label="Wannier functions: their atomic orbitals, per site (blank for a Slater-Koster model)" placeholder="e.g. pz px py"
              value={form.soc.labels} onChange={(e) => set({ soc: { ...form.soc!, labels: e.currentTarget.value } })} />
            <Text size="xs" c="dimmed">(λ/2) L·σ on the shell of every atom of the element - SKFitter&apos;s spin-orbit term - for a model from a
              calculation without spin-orbit coupling (e.g. a meta-GGA such as TB09 that the DFT code cannot run non-collinear). Atomic λ:
              Te 5p ≈ 0.5 eV, Bi 6p ≈ 1.2 eV. Wannier functions have no atomic label: name the orbital of each function of a site, in order
              (Wannier90 orders a p projection pz, px, py) - valid only for atom-centred projected functions with the global axes. Applied
              first, before a scissor; makes the model spinful.</Text>
          </Stack>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Scissor (move the conduction bands)" checked={form.scissor.on} onChange={(e) => set({ scissor: { ...form.scissor, on: e.currentTarget.checked } })} />
        {form.scissor.on && (
          <Stack gap={4} mt={4}>
            <Group grow gap="xs">
              <N label="δ" unit="eV" step={0.1} value={form.scissor.delta} onChange={(delta) => set({ scissor: { ...form.scissor, delta } })} />
              <N label="k-mesh per direction (0: automatic)" int min={0} value={form.scissor.mesh} onChange={(mesh) => set({ scissor: { ...form.scissor, mesh } })} />
              <N label="valence bands (0: those below E_F)" int min={0} value={form.scissor.nValence ?? 0} onChange={(nValence) => set({ scissor: { ...form.scissor, nValence } })} />
            </Group>
            <Text size="xs" c="dimmed">The DFT gap is often too small: δ moves every conduction state up. Exact on the k-mesh (automatic: 12 per
              direction, which holds H of a hexagonal cell and the other common points); the error between mesh points is reported. For a DFT
              semimetal (PBE Te: bands overlap across the zone), give the number of valence bands - Band edges reports it - and only a direct gap
              is needed. For a Slater-Koster fit, the scissor can also be set before fitting (the fit set-up).</Text>
          </Stack>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Exchange field (eV: a magnetic substrate or adatoms)" checked={!!form.exchange?.on}
          onChange={(e) => set({ exchange: { ...(form.exchange ?? defaultModify().exchange!), on: e.currentTarget.checked } })} />
        {form.exchange?.on && (
          <Stack gap={4} mt={4}>
            <Group grow gap="xs" align="flex-end">
              <N label="energy" unit="eV" step={0.05} value={form.exchange.energy} onChange={(energy) => set({ exchange: { ...form.exchange!, energy } })} />
              <Select size="xs" label="element" data={[{ value: "", label: "all" }, ...elements.map((e) => ({ value: e, label: e }))]}
                value={form.exchange.element} onChange={(v) => set({ exchange: { ...form.exchange!, element: v ?? "" } })} allowDeselect={false} />
            </Group>
            <Vec label="direction" unit="" step={1} value={form.exchange.direction} onChange={(direction) => set({ exchange: { ...form.exchange!, direction } })} />
            <Text size="xs" c="dimmed">+energy n·σ on every selected orbital: the size of a magnetic proximity effect (tens to hundreds
              of meV), far beyond a Zeeman field (10 T ≈ 1 meV). Makes the model spinful.</Text>
          </Stack>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Rashba spin-orbit coupling (a perpendicular field or substrate)" checked={!!form.rashba?.on}
          onChange={(e) => set({ rashba: { ...(form.rashba ?? defaultModify().rashba!), on: e.currentTarget.checked } })} />
        {form.rashba?.on && (
          <Stack gap={4} mt={4}>
            <Group grow gap="xs" align="flex-end">
              <N label="λ_R" unit="eV" step={0.01} value={form.rashba.lam} onChange={(lam) => set({ rashba: { ...form.rashba!, lam } })} />
              <TextInput size="xs" label="orbitals (labels)" value={form.rashba.orbitals}
                onChange={(e) => set({ rashba: { ...form.rashba!, orbitals: e.currentTarget.value } })} />
            </Group>
            <Text size="xs" c="dimmed">i λ_R (σ × d̂)_z on the nearest-neighbour bonds between these orbitals (Kane and Mele). With an
              exchange field, graphene becomes a quantum anomalous Hall insulator (C = ±2). Makes the model spinful.</Text>
          </Stack>
        )}
      </Card>
      <Card withBorder p="xs">
        <Checkbox label="Electric field (V/Å, across the vacuum of a layer or wire)" checked={!!form.efield?.on}
          onChange={(e) => set({ efield: { ...(form.efield ?? defaultModify().efield!), on: e.currentTarget.checked } })} />
        {form.efield?.on && (
          <Stack gap={4} mt={4}>
            <Vec label="E" unit="V/Å" step={0.05} value={form.efield.E} onChange={(E) => set({ efield: { ...form.efield!, E } })} />
            <Text size="xs" c="dimmed">+E·r on the electron (0.1 V/Å shifts levels 1 Å apart by 0.1 eV): a gate field across a bilayer, the
              Stark effect of a layer. Only across directions without hoppings - along the layer it is the bias of a device. A Wannier
              model from a run with its _tb.dat also gets the interband (Stark) terms; other models only the shift of each orbital centre.
              No screening: a fixed field overestimates the response.</Text>
          </Stack>
        )}
      </Card>
      {problems.map((p) => <Text key={p} size="xs" c="red">{p}</Text>)}
      <Group justify="space-between">
        <Group gap="xs">
          {mem.base_file && <Badge color="grape" variant="light">modified</Badge>}
          {changes.map((c) => <Badge key={c} variant="outline" size="sm">{c}</Badge>)}
        </Group>
        <Group gap="xs">
          {mem.base_file && <Button size="xs" variant="default" onClick={reset}>Reset to the model as built</Button>}
          <Button size="xs" onClick={() => apply.mutate()} loading={apply.isPending} disabled={problems.length > 0}>Apply changes</Button>
        </Group>
      </Group>
      {job && job.id === mem.modify_job && job.status !== "succeeded" && (
        <Stack gap={4}>
          <Group gap="xs"><JobStatusBadge status={job.status} /><Text size="sm">Changing the model</Text></Group>
          <JobProgress job={job} />
          {job.error && <ErrorBox error={new Error(job.error)} />}
        </Stack>
      )}
    </Stack>
  );
}

// -- band edges -----------------------------------------------------------------------------------------------------
function EdgesTable({ r }: { r: Record<string, any> }) {
  const e = r.edges;
  const ve = (r.valley_edges ?? {}) as Record<string, any>;
  const sp = (s?: number[]) => (s ? ` ⟨σz⟩ ${s[2].toFixed(2)}` : "");
  return (
    <Stack gap={4}>
      <Table fz="xs" withTableBorder data-testid="edges-table">
        <Table.Tbody>
          <Table.Tr><Table.Td>VBM</Table.Td><Table.Td>{fmtE(e.vbm.E)} at k {fmtK(e.vbm.k_frac)}{sp(e.vbm.spin)}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>CBM</Table.Td><Table.Td>{fmtE(e.cbm.E)} at k {fmtK(e.cbm.k_frac)}{sp(e.cbm.spin)}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Gap</Table.Td><Table.Td>{fmtE(e.gap)} ({e.overlap ? "bands overlap: semimetal" : e.gap_type})</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Smallest direct gap</Table.Td><Table.Td>{fmtE(e.direct_gap.E)} at k {fmtK(e.direct_gap.k_frac)}</Table.Td></Table.Tr>
          {Object.entries(ve).map(([name, v]) => (
            <Table.Tr key={name}><Table.Td>Valley {name}</Table.Td>
              <Table.Td>VB {fmtE(v.vb.E)}, CB {fmtE(v.cb.E)}, gap {fmtE(v.gap)}</Table.Td></Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed">k in fractions of the reciprocal lattice vectors; {r.n_valence} valence bands.</Text>
    </Stack>
  );
}

function SlopeTable({ r }: { r: Record<string, any> }) {
  const rows = slopeRows(r);
  if (!rows.length) return null;
  return (
    <Table fz="xs" withTableBorder data-testid="slopes-table">
      <Table.Thead><Table.Tr><Table.Th>Edge or gap</Table.Th><Table.Th>dE/dB (meV/T)</Table.Th><Table.Th>g = slope / μB</Table.Th></Table.Tr></Table.Thead>
      <Table.Tbody>{rows.map((x) => (
        <Table.Tr key={x.key}><Table.Td>{x.key}</Table.Td><Table.Td>{x.slope.toFixed(4)}</Table.Td><Table.Td>{x.g.toFixed(3)}</Table.Td></Table.Tr>
      ))}</Table.Tbody>
    </Table>
  );
}

export function EdgesResult({ r }: { r: Record<string, any> }) {
  const figs = useMemo(() => ({
    edges: r.rows ? sweepFigure(r, "edges") : null, gaps: r.rows ? sweepFigure(r, "gaps") : null,
    valleys: r.valley_edges && Object.keys(r.valley_edges).length ? valleyLevelsFigure(r) : null,
    zoom: r.bands ? bandZoomFigure(r) : null,
  }), [r]);
  return (
    <Stack gap="xs">
      {(r.warnings ?? []).map((w: string) => <Alert key={w} color="yellow" p="xs"><Text size="xs">{w}</Text></Alert>)}
      {figs.zoom && <Plot data={figs.zoom.data} layout={figs.zoom.layout} height={400} />}
      {figs.gaps && (
        <Tabs defaultValue="gaps">
          <Tabs.List><Tabs.Tab value="gaps">Gaps</Tabs.Tab><Tabs.Tab value="edges">Edges</Tabs.Tab></Tabs.List>
          <Tabs.Panel value="gaps"><Plot data={figs.gaps.data} layout={figs.gaps.layout} height={380} /></Tabs.Panel>
          <Tabs.Panel value="edges"><Plot data={figs.edges!.data} layout={figs.edges!.layout} height={380} /></Tabs.Panel>
        </Tabs>
      )}
      {r.analysis === "field" && <SlopeTable r={r} />}
      {r.edges && <EdgesTable r={r} />}
      {figs.valleys && <Plot data={figs.valleys.data} layout={figs.valleys.layout} height={340} />}
    </Stack>
  );
}

export function EdgesPanel({ project, mem, remember, edit, limits }: {
  project: ProjectDetail; mem: NegfMemory; remember: (m: Partial<NegfMemory>) => void; edit: (m: Partial<NegfMemory>) => void; limits: Limits;
}) {
  const qc = useQueryClient();
  const s = mem.summary!;
  const f = mem.edges ?? defaultEdges();
  const set = (next: Partial<EdgesForm>) => edit({ edges: { ...f, ...next } });
  const shownId = mem.edges_jobs?.[f.analysis];
  const { job } = useLiveJob(shownId);
  const problems = edgesProblems(f);
  const maxPts = limits["negf.max_field_points"];
  const run = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "negf.band_edges", project_id: project.id,
      params: { model_file: mem.model_file, request: edgesRequest(f) } }),
    onSuccess: (j) => {
      remember({ edges_jobs: { ...(mem.edges_jobs ?? {}), [f.analysis]: j.id } });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (e) => notifyError(e),
  });
  const valleyText = s.valleys && Object.keys(s.valleys).length
    ? `automatic: ${Object.keys(s.valleys).join(", ")} of the hexagonal lattice` : "automatic (none for this lattice)";
  const csv = job?.status === "succeeded" && job.result?.files?.csv;

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="xs">
          <SegmentedControl size="xs" fullWidth value={f.analysis} onChange={(v) => set({ analysis: v as Analysis })} data={ANALYSES} data-testid="analysis" />
          {(f.analysis === "edges" || f.analysis === "bands") && (
            <>
              <Vec label="B" unit="T" step={1} value={f.B} onChange={(B) => set({ B })} />
              {f.analysis === "bands" && <N label="Zoom: around the edges" unit="eV" step={0.05} value={f.window} onChange={(window) => set({ window })} />}
            </>
          )}
          {f.analysis === "field" && (
            <>
              <Group grow gap="xs">
                <N label="|B| from" unit="T" value={f.sweep.B_min} onChange={(B_min) => set({ sweep: { ...f.sweep, B_min } })} />
                <N label="to" unit="T" value={f.sweep.B_max} onChange={(B_max) => set({ sweep: { ...f.sweep, B_max } })} />
                <N label="points" int min={2} value={f.sweep.n} onChange={(n) => set({ sweep: { ...f.sweep, n } })} />
              </Group>
              <Select size="xs" label="Direction" allowDeselect={false} value={f.sweep.direction}
                onChange={(v) => set({ sweep: { ...f.sweep, direction: (v ?? "out-of-plane") as EdgesForm["sweep"]["direction"] } })}
                data={[{ value: "out-of-plane", label: s.two_d ? "Out of plane (layer normal)" : "z" },
                  { value: "in-plane", label: s.two_d ? "In plane, along a1" : "x" }, { value: "in-plane-2", label: s.two_d ? "In plane, across a1" : "y" }]} />
            </>
          )}
          {f.analysis === "efield" && (() => {
            const e = f.esweep ?? defaultEdges().esweep!;
            return (
              <>
                <Group grow gap="xs">
                  <N label="E from" unit="V/Å" step={0.05} value={e.E_min} onChange={(E_min) => set({ esweep: { ...e, E_min } })} />
                  <N label="to" unit="V/Å" step={0.05} value={e.E_max} onChange={(E_max) => set({ esweep: { ...e, E_max } })} />
                  <N label="points" int min={2} value={e.n} onChange={(n) => set({ esweep: { ...e, n } })} />
                </Group>
                <Text size="xs" c="dimmed" data-testid="efield-note">A uniform field across the layer (the efield modifier): a gate field
                  on bilayer graphene opens a gap at K. No screening - give the field inside the material (a gate&apos;s field is screened
                  to a fraction of it). Wannier models with position matrices include the interband terms.</Text>
              </>
            );
          })()}
          {f.analysis === "angle" && (
            <>
              <N label="|B|" unit="T" value={f.angles.B} onChange={(B) => set({ angles: { ...f.angles, B } })} />
              <Group grow gap="xs">
                <N label="θ from" unit="°" value={f.angles.min} onChange={(min) => set({ angles: { ...f.angles, min } })} />
                <N label="to" unit="°" value={f.angles.max} onChange={(max) => set({ angles: { ...f.angles, max } })} />
                <N label="points" int min={2} value={f.angles.n} onChange={(n) => set({ angles: { ...f.angles, n } })} />
              </Group>
              <N label="φ in the plane, from a1" unit="°" value={f.angles.phi} onChange={(phi) => set({ angles: { ...f.angles, phi } })} />
              <Text size="xs" c="dimmed">θ = 0° is out of plane, 90° in plane.</Text>
            </>
          )}
          {f.analysis !== "efield" && <Group gap="md">
            <Checkbox size="xs" label="spin Zeeman" checked={f.spin} onChange={(e) => set({ spin: e.currentTarget.checked })} />
            <Checkbox size="xs" label="orbital μB L·B" checked={f.orbital} onChange={(e) => set({ orbital: e.currentTarget.checked })} />
          </Group>}
          <Select size="xs" label="Valleys" allowDeselect={false} value={f.valleys} onChange={(v) => set({ valleys: (v ?? "auto") as EdgesForm["valleys"] })}
            data={[{ value: "auto", label: valleyText }, { value: "none", label: "none" }, { value: "custom", label: "my own k-points" }]} />
          {f.valleys === "custom" && <TextInput size="xs" placeholder="K = 2/3, 1/3, 0; K' = 1/3, 2/3, 0" value={f.custom}
            onChange={(e) => set({ custom: e.currentTarget.value })} />}
          {(f.analysis === "field" || f.analysis === "angle" || f.analysis === "efield") && <Text size="xs" c="dimmed">Your plan: up to {String(maxPts ?? "?")} points per sweep.
            {f.analysis === "efield" ? "" : " Zeeman shifts are about 0.06 meV per tesla per unit of g: the plots show changes in meV."}</Text>}
          {problems.map((p) => <Text key={p} size="xs" c="red">{p}</Text>)}
          <Group justify="flex-end">
            <Button size="xs" onClick={() => run.mutate()} loading={run.isPending} disabled={problems.length > 0}>Find the edges</Button>
          </Group>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <RunCard job={job} empty={<Text c="dimmed" size="sm">Choose an analysis and press Find the edges.</Text>}
          extra={csv ? <Button size="xs" variant="light" onClick={() => download(`/files/${csv}/download`, job!.result!.csv_name ?? "edges.csv").catch(notifyError)}>
            Download table (.csv)</Button> : undefined}>
          {job?.status === "succeeded" && job.result && <EdgesResult r={job.result} />}
        </RunCard>
        <PreviousRuns project={project} kind="negf.band_edges" current={shownId}
          onOpen={(id) => remember({ edges_jobs: { ...(mem.edges_jobs ?? {}), [f.analysis]: id } })} describe={describeEdges} />
      </Grid.Col>
    </Grid>
  );
}

export function ModelTools({ project, mem, remember, update, limits, product = "negf" }: {
  project: ProjectDetail; mem: NegfMemory; remember: (m: Partial<NegfMemory>) => void; update?: (m: Partial<NegfMemory>) => void; limits: Limits;
  product?: "negf" | "properties";
}) {
  const edit = update ?? remember;
  if (product !== "negf")              // Properties has its own band tools (electronic family): only the modifiers here
    return <Card withBorder data-testid="model-tools"><Text fw={600} size="sm" mb="xs">Modify model</Text>
      <ModifyPanel project={project} mem={mem} remember={remember} edit={edit} product={product} /></Card>;
  return (
    <Card withBorder data-testid="model-tools">
      <Tabs defaultValue="edges">
        <Tabs.List>
          <Tabs.Tab value="edges">Band edges</Tabs.Tab>
          <Tabs.Tab value="modify">Modify model</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="edges" pt="sm"><EdgesPanel project={project} mem={mem} remember={remember} edit={edit} limits={limits} /></Tabs.Panel>
        <Tabs.Panel value="modify" pt="sm"><ModifyPanel project={project} mem={mem} remember={remember} edit={edit} /></Tabs.Panel>
      </Tabs>
    </Card>
  );
}
